import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { maxSelectableProductIndex } from "./productSuggestionIndices";
import type {
  SuggestionRowErrors,
  TextComposerSuggestedAction,
  TextComposerSuggestedActionType,
} from "./suggestedActionsSchema";
import {
  SUGGESTION_TYPE_LABELS,
  SUGGESTION_TYPES,
  createProductSuggestion,
  createSuggestedAction,
  formatPhoneNumber,
  suggestionTextHasMergeTag,
} from "./suggestedActionsSchema";
import { useMessageDraftStore } from "../../editor/messageDraftStoreFactory";
import { InlineLexicalInput } from "../../InlineLexicalInput";
import { useDefaultProductContextModel } from "../mergeTags/editor/useDefaultProductContextModel";
import { getUrlMergeTagPickerConfig } from "../mergeTags/mergeTagDefinitions";
import {
  guardedProductFieldDtl,
  parseGuardedProductFieldDtl,
} from "../mergeTags/productFieldDtl";

import { PRODUCT_GRID_MODELS } from "@/features/EmailEditor/plugins/productGridConstants";
import {
  ContentCopyIcon,
  DeleteOutlineIcon,
  DragIndicatorIcon,
  ExpandMoreIcon,
} from "@/icons";
import { ordinal } from "@/utils/ordinal";

const PRODUCT_MODEL_OPTIONS = PRODUCT_GRID_MODELS.filter(
  (option) => option.value !== "discover",
);

const PRODUCT_UI_TYPE = "PRODUCT";
const UI_SUGGESTION_TYPES = [...SUGGESTION_TYPES, PRODUCT_UI_TYPE] as const;
const UI_SUGGESTION_TYPE_LABELS: Record<string, string> = {
  ...SUGGESTION_TYPE_LABELS,
  [PRODUCT_UI_TYPE]: "Recommend Product",
};

function productRefOf(
  suggestion: TextComposerSuggestedAction,
): { model: string; index: number } | null {
  if (suggestion.type !== "OPEN_URL") return null;
  const ref = parseGuardedProductFieldDtl(suggestion.url);
  return ref?.field === "item_url"
    ? { model: ref.model, index: ref.index }
    : null;
}

interface SuggestionRowProps {
  id: string;
  /** 1-based position, shown in the header and used for a11y labels. */
  position: number;
  suggestion: TextComposerSuggestedAction;
  errors?: SuggestionRowErrors;
  disabled?: boolean;
  onChange: (next: TextComposerSuggestedAction) => void;
  /** Fires when a field loses focus, so the parent can reveal its error. */
  onFieldBlur?: (field: keyof SuggestionRowErrors) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

export function SuggestionRow({
  id,
  position,
  suggestion,
  errors,
  disabled = false,
  onChange,
  onFieldBlur,
  onDuplicate,
  onRemove,
}: SuggestionRowProps) {
  const [expanded, setExpanded] = useState(true);
  const urlMergeTags = useMemo(
    () => getUrlMergeTagPickerConfig({ includeProductAliases: false }),
    [],
  );
  const defaultModel = useDefaultProductContextModel();
  const bodyText = useMessageDraftStore((s) => s.authoringState.bodyText ?? "");
  const allSuggestions =
    useMessageDraftStore((s) => s.authoringState.suggestions) ?? [];

  const productRef = productRefOf(suggestion);
  const uiType = productRef ? PRODUCT_UI_TYPE : suggestion.type;

  const productMaxIndex = maxSelectableProductIndex({
    bodyText,
    defaultModel,
    model: productRef?.model ?? defaultModel,
    suggestions: allSuggestions,
    selfId: suggestion.id,
    selfIndex: productRef?.index ?? 0,
  });

  const repointProduct = (model: string, index: number) => {
    if (suggestion.type !== "OPEN_URL") return;
    onChange({
      ...suggestion,
      url: guardedProductFieldDtl(model, index, "item_url"),
      text: guardedProductFieldDtl(model, index, "name"),
    });
  };
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const label = UI_SUGGESTION_TYPE_LABELS[uiType];
  const modelLabel = productRef
    ? (PRODUCT_MODEL_OPTIONS.find((o) => o.value === productRef.model)?.label ??
      productRef.model)
    : undefined;
  const headerText = productRef
    ? `Suggestion ${position} – ${label} · ${modelLabel}, ${ordinal(productRef.index + 1)} pick`
    : `Suggestion ${position} – ${label}`;

  return (
    <Box
      ref={setNodeRef}
      // Translate only — rows vary in height, so Transform's scaleX/scaleY
      // would zoom the dragged row and throw off drop targeting.
      style={{ transform: CSS.Translate.toString(transform), transition }}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadiusSm}px`,
        // Hidden while dragging — the DragOverlay renders the lifted copy that
        // follows the cursor; this row's slot stays as an empty gap.
        visibility: isDragging ? "hidden" : "visible",
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 2,
        }}
      >
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          aria-label={`Reorder suggestion ${position}`}
          disabled={disabled}
          sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {headerText}
        </Typography>

        <IconButton
          size="small"
          aria-label={`Duplicate suggestion ${position}`}
          onClick={onDuplicate}
          disabled={disabled}
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Remove suggestion ${position}`}
          onClick={onRemove}
          disabled={disabled}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`${expanded ? "Collapse" : "Expand"} suggestion ${position}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 150ms",
            }}
          />
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 3,
          })}
        >
          <TextField
            select
            size="small"
            fullWidth
            value={uiType}
            disabled={disabled}
            onChange={(event) =>
              onChange(
                event.target.value === PRODUCT_UI_TYPE
                  ? createProductSuggestion(defaultModel)
                  : createSuggestedAction(
                      event.target.value as TextComposerSuggestedActionType,
                    ),
              )
            }
            slotProps={{
              htmlInput: { "aria-label": `Suggestion ${position} type` },
            }}
          >
            {UI_SUGGESTION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {UI_SUGGESTION_TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </TextField>

          {!productRef && (
            <>
              <Typography variant="body3" color="text.secondary">
                Text
              </Typography>
              <InlineLexicalInput
                value={suggestion.text}
                onChange={(text) => onChange({ ...suggestion, text })}
                onBlur={() => onFieldBlur?.("text")}
                surface="textComposer"
                placeholder="Enter text"
                editable={!disabled}
                error={errors?.text !== undefined}
                helperText={
                  errors?.text ??
                  (suggestionTextHasMergeTag(suggestion.text)
                    ? "Merge tags vary in length, so the sent button text may differ."
                    : undefined)
                }
              />
            </>
          )}

          {suggestion.type === "OPEN_URL" && !productRef && (
            <>
              <Typography variant="body3" color="text.secondary">
                Website URL
              </Typography>
              <InlineLexicalInput
                value={suggestion.url}
                onChange={(url) => onChange({ ...suggestion, url })}
                onBlur={() => onFieldBlur?.("url")}
                surface="textComposer"
                allowedTags={urlMergeTags.allowedTags}
                extraCategories={urlMergeTags.extraCategories}
                placeholder="https:// or http://"
                editable={!disabled}
                error={errors?.url !== undefined}
                helperText={errors?.url}
              />
            </>
          )}

          {suggestion.type === "DIAL_PHONE" && (
            <>
              <Typography variant="body3" color="text.secondary">
                Phone number
              </Typography>
              <TextField
                placeholder="+1 (555) 000-0000"
                value={suggestion.phoneNumber}
                onChange={(event) =>
                  onChange({ ...suggestion, phoneNumber: event.target.value })
                }
                // Prettify a valid number once the marketer leaves the field,
                // never mid-entry.
                onBlur={(event) => {
                  onChange({
                    ...suggestion,
                    phoneNumber: formatPhoneNumber(event.target.value),
                  });
                  onFieldBlur?.("phoneNumber");
                }}
                variant="outlined"
                size="small"
                fullWidth
                disabled={disabled}
                error={errors?.phoneNumber !== undefined}
                helperText={errors?.phoneNumber}
                // `&&` outweighs the global MuiOutlinedInput theme border override.
                sx={{
                  "&& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                }}
                slotProps={{
                  htmlInput: {
                    "aria-label": `Suggestion ${position} phone number`,
                    inputMode: "tel",
                    "data-1p-ignore": true,
                  },
                }}
              />
            </>
          )}

          {productRef && (
            <>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body3" color="text.secondary">
                    Recommend from
                  </Typography>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={productRef.model}
                    disabled={disabled}
                    onChange={(event) => {
                      const model = event.target.value;
                      const maxForModel = maxSelectableProductIndex({
                        bodyText,
                        defaultModel,
                        model,
                        suggestions: allSuggestions,
                        selfId: suggestion.id,
                      });
                      repointProduct(
                        model,
                        Math.min(productRef.index, maxForModel),
                      );
                    }}
                    slotProps={{
                      htmlInput: {
                        "aria-label": `Suggestion ${position} recommend from`,
                      },
                    }}
                  >
                    {PRODUCT_MODEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="body3" color="text.secondary">
                    Which pick
                  </Typography>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={productRef.index}
                    disabled={disabled}
                    onChange={(event) =>
                      repointProduct(
                        productRef.model,
                        Number(event.target.value),
                      )
                    }
                    slotProps={{
                      htmlInput: {
                        "aria-label": `Suggestion ${position} which pick`,
                      },
                    }}
                  >
                    {Array.from({ length: productMaxIndex + 1 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {`${ordinal(i + 1)} recommended`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <Typography variant="body3" color="text.secondary">
                The button shows the product&apos;s name and opens its product
                page when tapped — like Open URL.
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
